import { useSyncExternalStore } from 'react';
import { comparisonWindowEnd, priorPeriodWindow } from '../../ReportingV2/hooks/usePriorPeriod';
import { calendarDate } from '../../ReportingV2/lib/freshnessMessage';

/**
 * Custom Dashboards has no single always-fetched "totals"/freshness widget
 * the way Unified Reporting's Redux slice does (same gap #5054 found for
 * permissions) — a dashboard is an arbitrary set of user-configured widgets,
 * each of which gets its own `freshness` map back on every report response
 * (`reportingV2.api.ts`'s `ReportResponse.freshness`). Accumulated
 * module-level, merged rather than replaced, since different widgets query
 * different dsp scopes and each response only ever carries freshness for the
 * dsps IT touched — same pattern as `labelCache` in useFilterOptions.ts and
 * `dashboardPermissions.ts`'s `restrictedFieldsSeen`.
 *
 * Persists across dashboard navigation within a session (never reset) — a
 * platform's last-reported date doesn't change per-dashboard.
 */
let freshness: Record<string, string> = {};
let version = 0;
const listeners = new Set<() => void>();

export const recordFreshness = (next: Record<string, string> | undefined): void => {
  if (!next || Object.keys(next).length === 0) return;
  // CodeRabbit, flank#5065: every report response carries freshness for the
  // dsps it touched, so this runs on nearly every widget fetch — most of
  // which report a date this store already has. Bumping `version`
  // unconditionally re-triggered every `useDashboardFreshness` subscriber
  // (and, via the derived `comparisonEnd` above, every widget's own fetch
  // effect) on each of those no-op merges.
  const changed = Object.entries(next).some(([dsp, date]) => freshness[dsp] !== date);
  if (!changed) return;
  freshness = { ...freshness, ...next };
  version += 1;
  listeners.forEach(l => l());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getVersion = (): number => version;

/** Reset for tests only — the module-level store otherwise never clears. */
export const __resetDashboardFreshnessForTest = (): void => {
  freshness = {};
  version = 0;
};

/** Reads the store for tests only — `useDashboardFreshness` itself needs a
 * React rendering harness this directory's tests don't have (same gap noted
 * in dashboardPermissions.test.ts). */
export const __getFreshnessForTest = (): { freshness: Record<string, string>; version: number } => ({
  freshness: { ...freshness },
  version,
});

export const useDashboardFreshness = (): Record<string, string> => {
  useSyncExternalStore(subscribe, getVersion);
  return freshness;
};

/**
 * "Data through <date>" next to the dashboard's own date range control
 * (flank#5049) — the same cutoff `comparisonWindowEnd` clamps comparison
 * windows to, formatted the same way Unified Reporting's healthy-path
 * freshness banner does (`freshnessMessage.ts`'s `Data through ${calendarDate(...)}`).
 * Deliberately NOT Unified Reporting's full warning/stale-platform-naming
 * banner (`freshnessMessage`) — flank#5049's AC asks only for this one label
 * next to the date range, not that separate, more elaborate system; `null`
 * when nothing has reported anything yet (freshness map still empty) rather
 * than a misleading date.
 */
export const dataThroughLabel = (
  dateStart: string,
  dateEnd: string,
  dsps: string[],
  freshnessMap: Record<string, string>,
): string | null => {
  if (Object.keys(freshnessMap).length === 0) return null;
  const cutoff = comparisonWindowEnd(dateStart, dateEnd, dsps, freshnessMap);
  return `Data through ${calendarDate(cutoff)}`;
};

export { comparisonWindowEnd, priorPeriodWindow };
